import { inject, Injectable } from '@angular/core';

import { WindowService } from 'src/shared/providers/window.service';
import { PosthogService } from 'src/shared/services/posthog.service';

import { FEATURE_FLAGS, FEATURE_FLAG_DEFAULTS } from 'src/shared/constants/feature-flags';

import { environment } from 'src/environments/environment';

/**
 * Resolves + tracks the PostHog "pm-demo-account-manager" experiment, which varies what a
 * property-manager/host demo user sees right after creating their account (Get Started
 * flow Step 2). Variants:
 *   control            → current Meet Your Account Manager page
 *   salesy-v1          → new MYAM design (product value props)
 *   salesy-v2          → new MYAM design (social proof + concierge services)
 *   salesy-v3          → new MYAM design
 *   skip-to-schedule   → skip MYAM, go straight to the Schedule Setup Call page
 *
 * Exposure ($feature_flag_called) is recorded when the user logs into the shared demo
 * account (recordExposure(), called from DemoAccountService.loginToDemoAccount) — i.e. the
 * same entry point as the landing experiment. KPI is scheduling a setup call. Funnel
 * events fire later: viewed_salesy_page (the salesy/MYAM page is shown), viewed_scheduled_call
 * (the schedule-call step), scheduled_call (booking). Mirrors PmDemoLandingService:
 * anonymous demo visitors are bucketed per-device, the variant is cached in localStorage
 * (posthog_variant_*, preserved across the demo→real conversion), and the flag is read
 * WITHOUT auto-firing exposure (getFeatureFlagNoExposure). Prod vs sandbox is chosen purely
 * by getFlagKey(); the flags carry NO targeting (anonymous demo users have no `environment`
 * person property — see [[feature_pm_demo_landing_experiment]]).
 */
@Injectable({ providedIn: 'root' })
export class PmDemoAccountManagerService {

  private posthogService = inject(PosthogService);
  private windowService = inject(WindowService);

  private getFlagKey(): string {
    return environment.environment === 'production'
      ? FEATURE_FLAGS.PM_DEMO_ACCOUNT_MANAGER
      : FEATURE_FLAGS.PM_DEMO_ACCOUNT_MANAGER_SANDBOX;
  }

  /** The currently bucketed variant (from sticky cache), for tagging funnel events. */
  getVariant(): string {
    const flagKey = this.getFlagKey();
    return localStorage.getItem(`posthog_variant_${flagKey}`)
      || (FEATURE_FLAG_DEFAULTS[flagKey] as string);
  }

  /** Warm the variant cache early (no exposure) so the post-account-creation branch is instant. */
  prefetchVariant(): void {
    this.resolveVariant().catch(() => { /* warm-up only — never block the flow */ });
  }

  /**
   * Record experiment exposure ($feature_flag_called) — call when the user logs into the
   * shared demo account (clicking "For Property Managers" or hitting /create-account/rental).
   * Resolves + caches the variant first so the post-account-creation branch is instant.
   */
  async recordExposure(): Promise<void> {
    const variant = await this.resolveVariant();
    this.posthogService.capture('$feature_flag_called', {
      $feature_flag: this.getFlagKey(),
      $feature_flag_response: variant,
    });
  }

  async resolveVariant(): Promise<string> {
    const flagKey = this.getFlagKey();
    const cacheKey = `posthog_variant_${flagKey}`;

    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      return cached;
    }

    await this.posthogService.waitForIdentification();
    await this.posthogService.waitForFlags();
    // No-exposure read: evaluating the flag must NOT enroll the user. Exposure is
    // recorded only by captureExposure() on account creation.
    const raw = await this.posthogService.getFeatureFlagNoExposure(flagKey);
    const variant = (raw as string) || (FEATURE_FLAG_DEFAULTS[flagKey] as string);

    if (raw !== null && raw !== undefined && raw !== false) {
      localStorage.setItem(cacheKey, variant);
    }

    return variant;
  }

  /** Capture a funnel event tagged with the resolved variant and the platform form factor. */
  capture(event: string, properties: Record<string, any> = {}): void {
    this.posthogService.capture(event, {
      variant: this.getVariant(),
      is_desktop: this.windowService.isDesktopRes,
      ...properties,
    });
  }
}
